import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from "react";

import { cn } from "@/lib/utils";

/**
 * HTML's own `prefix` attribute is a string, which would narrow ours.
 */
type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> & {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /**
   * `className` styles the bordered container; the native `<input>` itself
   * (and therefore its `::placeholder`) has no styling hook otherwise.
   */
  inputClassName?: string;
};

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      label,
      helperText,
      error,
      prefix,
      suffix,
      className,
      inputClassName,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hasError = !!error;

    return (
      <div className="flex w-full flex-col gap-1">
        {label && (
          <label
            htmlFor={inputId}
            className="text-text text-[13px] font-medium"
          >
            {label}
          </label>
        )}
        <div
          className={cn(
            "flex h-9 w-full items-center gap-2",
            "bg-card overflow-hidden rounded-lg",
            "border-border-strong border",
            "shadow-[inset_0_1px_0_rgba(0,0,0,0.04)]",
            "text-text text-sm",
            "transition-[box-shadow,border-color] duration-150",
            "focus-within:border-ring focus-within:ring-ring/30 focus-within:ring-2",
            hasError && "border-destructive focus-within:ring-destructive/30",
            disabled && "bg-surface-subdued opacity-70",
            className
          )}
        >
          {prefix && <span className="text-subdued pl-3">{prefix}</span>}
          <input
            id={inputId}
            ref={ref}
            disabled={disabled}
            className={cn(
              "placeholder:text-disabled h-full w-full bg-transparent outline-none",
              "disabled:cursor-not-allowed",
              !prefix && "pl-3",
              !suffix && "pr-3",
              inputClassName
            )}
            {...props}
          />
          {suffix && <span className="text-subdued pr-3">{suffix}</span>}
        </div>
        {hasError && <span className="text-destructive text-xs">{error}</span>}
        {!hasError && helperText && (
          <span className="text-subdued text-xs">{helperText}</span>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
